import { useEffect, useState } from "react";
import { Trash2, Download, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { fileUrl } from "@/lib/api";

export default function Library() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/library");
            setItems(data.items || []);
        } catch (_e) {
            toast.error("Failed to load library");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        try {
            await api.delete(`/library/${id}`);
            setItems((arr) => arr.filter((x) => x.id !== id));
            toast.success("Removed from library");
        } catch (_e) {
            toast.error("Failed to remove");
        }
    };

    const filtered = items.filter((i) => filter === "all" ? true : i.kind === filter);

    return (
        <AppShell subtitle="Library" title="Your enhanced gallery.">
            <div className="flex gap-2 mb-8">
                {[
                    { id: "all", label: "All" },
                    { id: "enhance", label: "Enhancements" },
                    { id: "background", label: "Backgrounds" },
                ].map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => setFilter(f.id)}
                        data-testid={`library-filter-${f.id}`}
                        className={`px-4 py-2 rounded-full text-sm border transition ${filter === f.id ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8"}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <div key={i} className="aspect-square rounded-2xl shimmer bg-zinc-900" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="glass-card rounded-2xl p-14 text-center" data-testid="library-empty">
                    <ImageIcon className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                    <div className="font-heading text-lg font-semibold mb-2">Nothing here yet</div>
                    <div className="text-sm text-zinc-500 mb-5">Your enhanced images will live here.</div>
                    <Link to="/enhance" className="btn-magic px-5 py-2.5 rounded-full text-white font-semibold inline-flex items-center gap-2">
                        Start enhancing
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                    {filtered.map((it) => (
                        <div key={it.id} className="rounded-2xl overflow-hidden border border-white/8 bg-zinc-900 lift group" data-testid={`library-item-${it.id}`}>
                            <div className="aspect-square overflow-hidden">
                                <img src={fileUrl(it.enhanced_path)} alt={it.preset} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            </div>
                            <div className="p-4">
                                <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">{it.kind}</div>
                                <div className="text-sm font-semibold mt-1 truncate capitalize">{it.preset.replace(/_/g, " ")}</div>
                                <div className="flex items-center gap-2 mt-3">
                                    <a
                                        href={fileUrl(it.enhanced_path)}
                                        download={`foodglow-${it.id}.png`}
                                        className="flex-1 text-center px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs inline-flex items-center justify-center gap-1.5"
                                    >
                                        <Download className="w-3 h-3" /> Download
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => remove(it.id)}
                                        data-testid={`library-delete-${it.id}`}
                                        className="px-3 py-2 rounded-lg bg-white/5 hover:bg-rose-500/15 border border-white/10 hover:border-rose-500/30 text-zinc-500 hover:text-rose-400 text-xs"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AppShell>
    );
}
